import React, { useState, useCallback } from "react";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import {
  ACTIVATE_ACCOUNT_PAGE,
  EMPTY_STRING,
  FORGOT_PASSWORD_PAGE,
  MFA_CHALLENGE_PAGE,
  MFA_SETUP,
  NEW_PASSWORD_REQUIRED,
  NIMIS_WEBSITE_URL,
  PASSWORD_RESET_REQUIRED_ERROR,
  REQUEST_INVITE_PAGE,
  SOFTWARE_TOKEN_MFA,
} from "@/constants/definitions";
import { loadDashboard } from "@/utils/navigation";

const INVALID_USERNAME_ERROR = "A username is required";
const INVALID_PASSPHRASE_ERROR = "A passphrase is required";

// Which route rendered this form. Both the normal sign-in (LOGIN_PAGE, "/") and
// the first-login activation (NEW_USER_LOGIN_PAGE, "/welcome") share this form,
// because Cognito requires the invitee to sign in with the temporary password
// from their email BEFORE they can set a permanent one. "activate" only swaps
// the copy so /welcome reads as an activation step, not a duplicate login page.
type LoginMode = "signIn" | "activate";

const SIGN_IN_HEADING = "Sign in to your account";
const ACTIVATE_HEADING = "Activate your account";
const ACTIVATE_SUBTEXT =
  "Enter the temporary passphrase from your invitation email to continue.";

interface LoginProps {
  cognitologin: any;
  setGraphDetails: (
    apiurl: string,
    region: string,
    realtimeEndpoint: string,
    graphQLHost: string
  ) => void;
  getUserDetails: () => Promise<void>;
  setUsername?: (username: string, oldpassphrase: string) => void;
  // Router navigate. Accepts an optional options object so callers can carry
  // route state (used to pass the MFA mode to the /mfa page).
  navigate: (path: string, options?: { state?: unknown; replace?: boolean }) => void;
  /** Fired the instant login succeeds (before the data load) to flip the
   *  background network into its intensified "loading" state, so the login card
   *  dissolves into an energised network rather than a faint one. */
  onRevealStart?: () => void;
  /** Triggers the neural-network bloom animation in AppRoutes.
   *  Returns a Promise that resolves when the screen is white. */
  onTransitionStart?: () => Promise<void>;
  /** Which route rendered this form. "activate" (the /welcome first-login flow)
   *  shows activation-specific copy; defaults to the normal sign-in. */
  mode?: LoginMode;
}

export default function Login({
  cognitologin,
  getUserDetails,
  setUsername,
  navigate,
  onRevealStart,
  onTransitionStart,
  mode = "signIn",
}: LoginProps) {
  const [username, setUsernameValue] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Route-specific copy: /welcome (first-login activation) vs / (normal sign-in).
  const isActivateMode = mode === "activate";
  const headingText = isActivateMode ? ACTIVATE_HEADING : SIGN_IN_HEADING;
  const subtext = isActivateMode ? ACTIVATE_SUBTEXT : null;

  const handleLogin = useCallback(async () => {
    if (!username) {
      setErrorMessage(INVALID_USERNAME_ERROR);
      return;
    }
    if (!passphrase) {
      setErrorMessage(INVALID_PASSPHRASE_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      const loginResponse = await cognitologin.login(username, passphrase);

      if (loginResponse === EMPTY_STRING) {
        setIsLoading(false);
        setIsTransitioning(true);
        // Bring the background network to life as the login card dissolves, so
        // the user transitions *into* an energised network (not a faint one)
        // and the data wait that follows reads as purposeful rather than idle.
        onRevealStart?.();

        // Reveal the ambient neural-network animation (the login card fades
        // away) and load the dashboard data underneath it, THEN play the bloom
        // finale — instead of running the load and the bloom in parallel.
        //
        // Previously both started together. The bloom swells to white in a
        // fixed ~2.4s, but the data load often takes longer; when it did, the
        // bloom finished and the screen held on solid white for the remaining
        // seconds, reading as a broken/blank screen. Loading first keeps the
        // moving network on screen for the whole wait, and deferring the bloom
        // lands the swell-to-white right before the populated dashboard appears.
        // (It also gives the fire-and-forget enrichment queries kicked off
        // inside getUserDetails the full bloom duration to settle.)
        await getUserDetails();
        await (onTransitionStart?.() ?? Promise.resolve());
        loadDashboard(navigate);
      } else if (loginResponse === NEW_PASSWORD_REQUIRED) {
        // A NEW_PASSWORD_REQUIRED challenge always means "first login — set your
        // initial password", so always route to the activation flow (which runs
        // signIn + completeNewPassword). This holds for BOTH the normal login and
        // the /welcome new-user login. The previous `/update` fallback was wrong:
        // that page (ChangePassword) needs an already-authenticated user and
        // cannot complete the challenge, so an invited user landing on the normal
        // login got silently bounced back here. `setUsername` carries the
        // credentials to the activation page (now passed to both login routes).
        setUsername?.(username, passphrase);
        navigate(ACTIVATE_ACCOUNT_PAGE);
      } else if (loginResponse === PASSWORD_RESET_REQUIRED_ERROR) {
        navigate(FORGOT_PASSWORD_PAGE);
      } else if (loginResponse === SOFTWARE_TOKEN_MFA || loginResponse === MFA_SETUP) {
        // Password accepted, second factor still owed. Route to /mfa. "setup"
        // enrols a first-time TOTP factor (existing users on first login under
        // REQUIRED, or a fresh invitee); "verify" asks an enrolled user for the
        // current code. The pending challenge user lives in cognitologin.
        navigate(MFA_CHALLENGE_PAGE, {
          state: {
            mode: loginResponse === MFA_SETUP ? "setup" : "verify",
            accountLabel: username,
          },
        });
      } else {
        setErrorMessage(loginResponse);
      }
    } catch (loginError) {
      console.error("Login failed:", loginError);
      setErrorMessage("An unexpected error occurred. Please try again.");
      setIsTransitioning(false);
    } finally {
      setIsLoading(false);
    }
  }, [username, passphrase, cognitologin, getUserDetails, navigate, setUsername, onRevealStart, onTransitionStart]);

  const handleKeyDown = useCallback(
    (keyEvent: React.KeyboardEvent) => {
      if (keyEvent.key === "Enter") {
        handleLogin();
      }
    },
    [handleLogin]
  );

  return (
    <div
      data-theme="dark"
      className="relative"
      style={{
        // When transitioning, fade the semi-opaque overlay out — alpha → 0 of the
        // SAME color (not the `transparent` keyword), so it interpolates
        // reliably rather than snapping/greying. Kept quick so it clears fast and
        // the camera zoom-in (which starts immediately, behind this) is the star.
        background: isTransitioning ? "hsla(222, 70%, 8%, 0)" : "hsla(222, 70%, 8%, 0.85)",
        transition: "background 450ms ease-out",
        minHeight: "calc(100vh + 1px)",
      }}
    >
      {/* Subtle gradient glow — fades during transition */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 20% 30%, rgba(59, 130, 246, 0.08) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 80% 70%, rgba(14, 165, 233, 0.05) 0%, transparent 70%)
          `,
          opacity: isTransitioning ? 0 : 1,
          transition: "opacity 350ms ease-out",
        }}
      />

      {/* Logo — fades out */}
      <div
        className="relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 sm:px-6 pt-6 pb-6"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transition: "opacity 350ms ease-out",
        }}
      >
        <a href={NIMIS_WEBSITE_URL}>
          <img
            src="/nimis-intelligence-white-logo.png"
            alt="NIMIS Intelligence"
            className="h-12 w-auto"
          />
        </a>
      </div>

      {/* Login card — fades out and drifts up when transitioning */}
      <div
        className="relative z-10 flex justify-center pt-[12vh]"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(-16px) scale(0.96)" : "translateY(0) scale(1)",
          transition: "opacity 400ms ease-out, transform 400ms ease-out",
        }}
      >
        <div className="w-full max-w-sm mx-4">
          <div className="rounded-xl bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] p-8">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-white">
                {headingText}
              </h1>
              {subtext && (
                <p className="mt-2 text-sm text-white/50 leading-relaxed">
                  {subtext}
                </p>
              )}
            </div>

            {errorMessage && (
              <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                {errorMessage}
              </div>
            )}

            <div className="space-y-4">
              <FormField label="Email" htmlFor="username" variant="dark">
                <Input
                  id="username"
                  type="text"
                  variant="dark"
                  placeholder="name@company.com"
                  value={username}
                  onChange={(changeEvent) => {
                    setUsernameValue(changeEvent.target.value);
                    setErrorMessage("");
                  }}
                  onKeyDown={handleKeyDown}
                  autoComplete="username"
                  disabled={isLoading || isTransitioning}
                  leftIcon={<Mail className="h-4 w-4" />}
                />
              </FormField>

              <FormField label="Passphrase" htmlFor="passphrase" variant="dark">
                <Input
                  id="passphrase"
                  type={showPassword ? "text" : "password"}
                  variant="dark"
                  placeholder="Enter your passphrase"
                  value={passphrase}
                  onChange={(changeEvent) => {
                    setPassphrase(changeEvent.target.value);
                    setErrorMessage("");
                  }}
                  onKeyDown={handleKeyDown}
                  autoComplete="current-password"
                  disabled={isLoading || isTransitioning}
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-white/30 hover:text-white/60 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />
              </FormField>

              <Button
                onClick={handleLogin}
                disabled={isLoading || isTransitioning}
                className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate(FORGOT_PASSWORD_PAGE)}
                className="text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                Forgot passphrase?
              </button>
            </div>

            {/* First-login recovery: a stranded invitee whose temporary password
                expired can request a fresh invite. Shown only on /welcome. */}
            {isActivateMode && (
              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => navigate(REQUEST_INVITE_PAGE)}
                  className="text-sm text-white/40 hover:text-white/70 transition-colors"
                >
                  Didn't get your invite? Request a new one
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
